import { createContext, useCallback, useContext, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { Bug } from "lucide-react";
import { detectModule } from "../lib/moduleRoutes.js";
import { IssueReportPanel } from "../components/IssueReportPanel";

interface IssuePanelPrefill {
  module?: string;
}

interface IssuePanelCtx {
  isOpen: boolean;
  open: (prefill?: IssuePanelPrefill) => void;
  close: () => void;
}

const IssuePanelContext = createContext<IssuePanelCtx>({
  isOpen: false,
  open: () => {},
  close: () => {},
});

export function useIssuePanel() {
  return useContext(IssuePanelContext);
}

export function IssuePanelProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [prefill, setPrefill] = useState<IssuePanelPrefill>({});
  const { pathname } = useLocation();

  const open = useCallback((p: IssuePanelPrefill = {}) => {
    const module = p.module ?? detectModule(pathname);
    setPrefill({ ...p, ...(module ? { module } : {}) });
    setIsOpen(true);
  }, [pathname]);

  const close = useCallback(() => setIsOpen(false), []);

  return (
    <IssuePanelContext.Provider value={{ isOpen, open, close }}>
      {children}
      <button
        id="hrms-report-btn"
        onClick={() => open()}
        title="Report Issue"
        style={{
          position: "fixed",
          bottom: 90,
          right: 24,
          zIndex: 800,
          width: 48,
          height: 48,
          borderRadius: "50%",
          background: "var(--color-brand-500, #6366f1)",
          color: "#fff",
          border: "none",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
          fontSize: 20,
        }}
      >
        <Bug size={20} />
      </button>
      <IssueReportPanel isOpen={isOpen} onClose={close} prefill={prefill} />
    </IssuePanelContext.Provider>
  );
}
